import { getEnv } from "@/config/env";

const EVIDENCE_BASE_API_URL = getEnv("VITE_EVIDENCE_BASE_API_URL") || "http://localhost:8000";

let getTokenFn: (() => Promise<string | null>) | null = null;

export function setEvidenceBaseTokenGetter(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

async function evidenceBaseApiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getTokenFn ? await getTokenFn() : null;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(`${EVIDENCE_BASE_API_URL}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `API error: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

const api = {
  get: <T>(path: string) => evidenceBaseApiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    evidenceBaseApiFetch<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    evidenceBaseApiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => evidenceBaseApiFetch<T>(path, { method: "DELETE" }),
};

export interface EvidenceType {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SourceType {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DuplicateCheckItem {
  title: string;
  filename: string;
  authors: string;
  country: string;
  year: string;
  /** Scopes the match: a Client upload to that client, anything else to the
   *  caller's own rows plus visibleIds below. */
  ownership: string | null;
  client_id: string | null;
}

export interface DuplicateCheckResult {
  index: number;
  is_duplicate: boolean;
  matching_document_id: string | null;
}

export const evidenceBaseApi = {
  // Evidence Types operations
  evidenceTypes: (): Promise<EvidenceType[]> => api.get("/api/evidence/evidence-types"),

  createEvidenceType: (data: { id: string; name: string; is_active?: boolean }): Promise<EvidenceType> =>
    api.post("/api/evidence/evidence-types", data),

  updateEvidenceType: (id: string, data: Partial<{ name: string; is_active: boolean }>): Promise<EvidenceType> =>
    api.put(`/api/evidence/evidence-types/${id}`, data),

  deleteEvidenceType: (id: string): Promise<void> =>
    api.delete(`/api/evidence/evidence-types/${id}`),

  // Source Types operations
  sourceTypes: (): Promise<SourceType[]> => api.get("/api/evidence/source-types"),

  createSourceType: (data: { id: string; name: string; is_active?: boolean }): Promise<SourceType> =>
    api.post("/api/evidence/source-types", data),

  updateSourceType: (id: string, data: Partial<{ name: string; is_active: boolean }>): Promise<SourceType> =>
    api.put(`/api/evidence/source-types/${id}`, data),

  deleteSourceType: (id: string): Promise<void> =>
    api.delete(`/api/evidence/source-types/${id}`),

  // Duplicate detection
  /** visibleIds: documents linked to the project being uploaded to. Private
   *  ownership is that link, so they count as visible whoever uploaded them. */
  checkDuplicates: async (items: DuplicateCheckItem[], visibleIds: string[] = []): Promise<DuplicateCheckResult[]> => {
    const response = await api.post<DuplicateCheckResult[] | { results?: DuplicateCheckResult[] }>("/api/evidence/documents/check-duplicates", { items, visible_ids: visibleIds });
    // Normalize: backend may return a raw array or a wrapper object like { results: [...] }
    if (Array.isArray(response)) return response;
    if (response && typeof response === "object" && "results" in response && Array.isArray(response.results)) {
      return response.results;
    }
    return [];
  },
};
